import { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { chargerHasTestSubmenu } from "../../utils/chargerGroups";

export type CloseWsFeedback = {
  kind: "success" | "warning" | "error" | "info";
  message: string;
};

export function ChargerActionMenu({
  chargerId,
  anchor,
  onOcpp,
  onWs,
  onCloseWs,
  closingWs,
  closeWsFeedback,
  onClose,
}: {
  chargerId: string;
  anchor: { x: number; y: number };
  onOcpp: () => void;
  onWs: () => void;
  onCloseWs: () => void;
  closingWs?: boolean;
  closeWsFeedback?: CloseWsFeedback | null;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testNote, setTestNote] = useState<string | null>(null);
  const hasTestSubmenu = chargerHasTestSubmenu(chargerId);

  useEffect(() => {
    const onPointerDown = (ev: MouseEvent) => {
      if (ref.current?.contains(ev.target as Node)) return;
      onClose();
    };
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") onClose();
    };
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const feedbackClass =
    closeWsFeedback?.kind === "success"
      ? "text-status-ok"
      : closeWsFeedback?.kind === "warning"
        ? "text-status-warn"
        : closeWsFeedback?.kind === "error"
          ? "text-status-error"
          : "text-text-secondary";

  const showTestPlaceholder = (label: string) => {
    setTestNote(`${label} — not implemented yet`);
  };

  return (
    <div
      ref={ref}
      className="fixed z-40 min-w-[12rem] max-w-[16rem] rounded-lg border border-border-subtle bg-card p-1 shadow-lg"
      style={{
        left: anchor.x,
        top: anchor.y,
      }}
      role="menu"
      aria-label={`Actions for ${chargerId}`}
    >
      <p
        className="px-2 py-1 font-medium text-text-primary"
        style={{ fontSize: "var(--font-micro)" }}
      >
        {chargerId}
      </p>
      <button
        type="button"
        role="menuitem"
        className="block w-full rounded px-2 py-1.5 text-left text-text-primary hover:bg-page"
        style={{ fontSize: "var(--font-body)" }}
        onClick={() => {
          onOcpp();
          onClose();
        }}
      >
        OCPP log
      </button>
      <button
        type="button"
        role="menuitem"
        className="block w-full rounded px-2 py-1.5 text-left text-text-primary hover:bg-page"
        style={{ fontSize: "var(--font-body)" }}
        onClick={() => {
          onWs();
          onClose();
        }}
      >
        WS log
      </button>

      {hasTestSubmenu ? (
        <>
          <button
            type="button"
            role="menuitem"
            aria-expanded={testOpen}
            className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-text-primary hover:bg-page"
            style={{ fontSize: "var(--font-body)" }}
            onClick={() => {
              setTestOpen((v) => !v);
              setTestNote(null);
            }}
          >
            Test
            <ChevronRight
              size={14}
              className={`text-text-secondary transition-transform ${testOpen ? "rotate-90" : ""}`}
            />
          </button>
          {testOpen && (
            <div className="ml-2 border-l border-border-subtle pl-1" role="group" aria-label="Test actions">
              <button
                type="button"
                role="menuitem"
                className="block w-full rounded px-2 py-1.5 text-left text-text-primary hover:bg-page"
                style={{ fontSize: "var(--font-micro)" }}
                onClick={() => showTestPlaceholder("Trigger StatusNotification")}
              >
                Trigger StatusNotification
              </button>
              <button
                type="button"
                role="menuitem"
                className="block w-full rounded px-2 py-1.5 text-left text-text-primary hover:bg-page"
                style={{ fontSize: "var(--font-micro)" }}
                onClick={() => showTestPlaceholder("Trigger MeterValues")}
              >
                Trigger MeterValues
              </button>
            </div>
          )}
        </>
      ) : (
        <button
          type="button"
          role="menuitem"
          disabled
          title="Tests available for BCU3/4 and PBLR only"
          className="block w-full cursor-not-allowed rounded px-2 py-1.5 text-left text-text-tertiary"
          style={{ fontSize: "var(--font-body)" }}
        >
          Test
        </button>
      )}

      {testNote && (
        <p className="px-2 py-1 text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
          {testNote}
        </p>
      )}

      <div className="my-1 border-t border-border-subtle" role="separator" />
      <button
        type="button"
        role="menuitem"
        disabled={closingWs}
        className="block w-full rounded px-2 py-1.5 text-left text-status-warn hover:bg-page disabled:cursor-not-allowed disabled:opacity-50"
        style={{ fontSize: "var(--font-body)" }}
        onClick={() => {
          onCloseWs();
        }}
      >
        {closingWs ? "Closing WS…" : "Close WS"}
      </button>
      {closeWsFeedback && (
        <p
          className={`px-2 py-1 ${feedbackClass}`}
          style={{ fontSize: "var(--font-micro)" }}
          role="status"
        >
          {closeWsFeedback.message}
        </p>
      )}
    </div>
  );
}
